import { t } from '../../i18n';
import { ImageIcon } from '../../shared/icons';
import type { ApiImage } from '../../shared/lib/media';
import {
  Card,
  CardHeading,
  FieldRow,
  Gap,
  ImageMark,
  ImagePair,
  Muted,
  Prose,
  ReadMore,
  StatusBadge,
  Strip,
  Thumb,
  type ThumbKind,
} from '../../shared/ui';

/**
 * «Было / стало» на уровне поля целиком.
 *
 * Пословной подсветки нет намеренно: правка биографии — это новая редакция
 * абзаца, а не набор вставок и удалений. Карта мелких различий внутри текста
 * читается хуже, чем два варианта подряд, и уводит разговор в запятые вместо
 * содержания.
 *
 * Компонент общий: его показывает и личный кабинет игрока («что уедет
 * модератору»), и экраны модерации («что игрок поменял»). Поэтому здесь нет ни
 * запросов, ни знания о том, чья это анкета, ни ссылок на маршруты кабинета —
 * только пропсы.
 *
 * Поле, которое не менялось, показывается одним значением: пара одинаковых
 * блоков подряд заставляет читателя сверять их глазами впустую.
 *
 * Своих внешних полей компонент не держит: поля и шаг между полями задаёт
 * список, в который его положили (CardList). Пока он рисовал собственный Div,
 * между соседними полями набегало 32 px вместо двенадцати.
 */

/** Как показывать значение. Текст и список способностей рисуются одинаково. */
export type FieldDiffKind = 'text' | 'image' | 'abilities';

/**
 * Одно поле в сравнении. Форма совпадает с FormFieldChangeView, который отдаёт
 * бэкенд (apps/api/src/modules/characters/characters.view.ts): подпись поля берётся
 * из FORM_FIELD_LABELS сервера, чтобы словарь не пришлось держать в двух местах.
 */
export interface FieldChange {
  field?: string;
  kind?: FieldDiffKind;
  label: string;
  /** Для kind='image' здесь идентификатор картинки строкой. */
  before: string | null;
  after: string | null;
  /** false — поле не менялось: рисуем только текущее значение. */
  changed?: boolean;
}

export interface FieldDiffProps extends FieldChange {
  /** Подпись верхнего блока. На модерации уместнее «В принятой версии». */
  beforeLabel?: string;
  afterLabel?: string;
  /**
   * Пометка «изменено» у заголовка. Нужна там, где изменённые поля стоят
   * вперемешку с нетронутыми: в списке, где изменено ВСЁ, она не различает
   * ничего и остаётся шумом.
   */
  mark?: boolean;
  /**
   * Нетронутое поле — одной строкой «подпись — значение», как в макете. Не
   * действует на картинку и на список способностей: в строку они не ложатся.
   */
  compact?: boolean;
  /** Чья вещь на снимке: пустой кадр носит её значок. */
  thumbKind?: ThumbKind;
}

export function FieldDiff({
  kind = 'text',
  label,
  before,
  after,
  changed = true,
  beforeLabel = t('character.diff.label.before'),
  afterLabel = t('character.diff.label.after'),
  mark = true,
  compact = false,
  thumbKind,
}: FieldDiffProps) {
  // Оба снимка поля уходят в галерею одним набором: старый и новый сличают,
  // листая их свайпом на весь экран, а не по паре миниатюр в вёрстке.
  const gallery = kind === 'image' ? imageGallery(before, after) : undefined;

  if (!changed) {
    // Картинке и списку способностей строка не годится: в неё влезает одно
    // значение в одну линию, а там кадр и несколько абзацев.
    if (compact && kind === 'text') {
      const value = after?.trim() ?? '';
      return (
        <FieldRow label={label} empty={value === ''}>
          {value === '' ? t('character.diff.value.empty') : value}
        </FieldRow>
      );
    }

    // Нетронутый снимок — тоже строка, а не блок с заголовком: в холсте
    // «остальное без изменений» набрано одинаково для всех полей. Сам кадр
    // при этом остаётся доступным — отметка открывает его на весь экран, а
    // сам снимок стоит в шапке разбора.
    if (compact && kind === 'image') {
      const imageId = after?.trim() ?? '';
      return (
        <FieldRow label={label} empty={imageId === ''}>
          {imageId === '' ? (
            t('character.diff.value.noPhoto')
          ) : (
            <ImageMark image={{ id: imageId }}>
              <ImageIcon size={11} />
              {t('character.diff.value.photo')}
            </ImageMark>
          )}
        </FieldRow>
      );
    }

    return (
      <div>
        <CardHeading size="s">{label}</CardHeading>
        <Gap step="s2" />
        <FieldValue kind={kind} value={after} alt={label} gallery={gallery} thumbKind={thumbKind} />
      </div>
    );
  }

  // Два снимка сличают взглядом, поэтому они стоят РЯДОМ. Засечки «было /
  // стало» тут не нужны: колонки и так подписаны, а две рамки вокруг двух
  // кадров превращали сравнение в набор коробок.
  if (kind === 'image') {
    return (
      <Card>
        <CardHeading
          size="s"
          badge={
            mark ? (
              <StatusBadge tone="pending">{t('character.diff.badge.changed')}</StatusBadge>
            ) : undefined
          }
        >
          {label}
        </CardHeading>

        <Gap step="s2h" />
        {/* Подписи те же, что у текстовых полей: на одном экране одна вещь
            не может называться двумя способами. В колонку шириной в половину
            карточки длинная из них не влезает и переносится на вторую строку —
            за выравнивание кадров отвечает сама пара. */}
        <ImagePair
          beforeLabel={beforeLabel}
          afterLabel={afterLabel}
          before={
            <FieldValue
              kind={kind}
              value={before}
              alt={label}
              gallery={gallery}
              thumbKind={thumbKind}
              fill
            />
          }
          after={
            <FieldValue
              kind={kind}
              value={after}
              alt={label}
              gallery={gallery}
              thumbKind={thumbKind}
              fill
            />
          }
        />
      </Card>
    );
  }

  return (
    <>
      {/* Поле целиком — одна карточка: пока это были две карточки подряд под
          общим заголовком, они читались как два разных поля. Что есть что,
          говорит засечка: приглушённая — прошлое, бирюзовая — предложенное. */}
      <Card>
        <CardHeading
          size="s"
          badge={
            mark ? (
              <StatusBadge tone="pending">{t('character.diff.badge.changed')}</StatusBadge>
            ) : undefined
          }
        >
          {label}
        </CardHeading>

        <Gap step="s2" />
        <Strip label={beforeLabel}>
          {/* Свёрнуто: в диффе биографии обе версии идут подряд, и развёрнутыми
              они занимают несколько экранов ещё до блока решения. */}
          <ReadMore>
            <FieldValue kind={kind} value={before} alt={label} gallery={gallery} />
          </ReadMore>
        </Strip>

        <Gap step="s1h" />
        <Strip label={afterLabel} tone="accent">
          <ReadMore>
            <FieldValue kind={kind} value={after} alt={label} gallery={gallery} />
          </ReadMore>
        </Strip>
      </Card>
    </>
  );
}

/** Список изменений подряд. Пустой список — не ошибка, просто ничего не изменилось. */
export function FieldDiffList({
  changes,
  beforeLabel,
  afterLabel,
}: {
  changes: FieldChange[];
  beforeLabel?: string;
  afterLabel?: string;
}) {
  return (
    <>
      {changes.map((change) => (
        <FieldDiff
          key={change.field ?? change.label}
          {...change}
          beforeLabel={beforeLabel}
          afterLabel={afterLabel}
          // В списке различий изменено каждое поле — помечать их все нечем.
          mark={false}
        />
      ))}
    </>
  );
}

function FieldValue({
  kind,
  value,
  alt,
  gallery,
  thumbKind,
  fill = false,
}: {
  kind: FieldDiffKind;
  value: string | null;
  alt: string;
  gallery?: ApiImage[];
  thumbKind?: ThumbKind;
  /** В паре колонок кадр тянется за колонкой, а не стоит фиксированным. */
  fill?: boolean;
}) {
  // В различиях анкеты картинка приходит публичным идентификатором-строкой.
  const image = value === null || value.trim() === '' ? null : { id: value };

  if (kind === 'image') {
    // Кадр стоит и без файла: в паре «было / стало» одна сторона была кадром,
    // а другая строчкой «Фото нет» — сравнивать было нечего с чем.
    return fill ? (
      <Thumb image={image} kind={thumbKind} fill alt={alt} gallery={gallery} />
    ) : (
      <Thumb image={image} kind={thumbKind} size="diff" alt={alt} gallery={gallery} />
    );
  }

  if (image === null) return <Muted>{t('character.diff.value.blank')}</Muted>;

  return <Prose>{value}</Prose>;
}

/** Обе версии снимка без повторов: у неизменённого поля это один и тот же файл. */
function imageGallery(before: string | null, after: string | null): ApiImage[] {
  const ids = [before, after].filter(
    (id): id is string => typeof id === 'string' && id.trim() !== '',
  );
  return [...new Set(ids)].map((id) => ({ id }));
}
